/**
 * Thin same-origin fetch wrapper with the US-A3 session lifecycle baked in: on a `401`
 * it transparently POSTs `/api/auth/refresh` **once** (single-flight across concurrent
 * calls) and retries the original request. A persistent `401` (refresh expired/invalid)
 * is surfaced so the shell can show the ReloadPrompt and re-mint the iframe.
 */

import { markSessionExpired } from "./sessionState.js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Shared in-flight refresh so concurrent 401s trigger exactly one refresh. */
let refreshing: Promise<boolean> | null = null;

function tryRefresh(): Promise<boolean> {
  refreshing ??= fetch("/api/auth/refresh", { method: "POST", credentials: "same-origin" })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const opts: RequestInit = { credentials: "same-origin", ...init };
  const res = await fetch(path, opts);
  if (res.status !== 401) return res;
  // Access token likely expired — refresh once, then retry the original request.
  const refreshed = await tryRefresh();
  return refreshed ? fetch(path, opts) : res;
}

/** apiFetch + JSON: returns parsed body on success, throws ApiError (with body) otherwise. */
export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(path, init);
  if (res.status === 401) markSessionExpired(); // survived a refresh → tell the shell to reload
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => undefined);
  if (!res.ok) {
    const message =
      (body && typeof body === "object" && "message" in body && String((body as { message: unknown }).message)) ||
      `request failed: ${res.status}`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}
